import { monthName } from '@sl/core';
import type { IsoDate, PeriodPreset, ResolvedPeriod } from '@sl/core';

// **WHAT THE CLOSED DATE CHIP IS CALLED WHEN THE DATA'S CLOCK IS BEHIND THE DEVICE'S** — REQ-021 AC6 (v2.5,
// T-315 / D-704), the one gap D-589 left open and the PM closed.
//
// D-589 made "now" the DATA's clock: the presets resolve against `account.today` and the header renders the
// served range (D-610). That fixed every FIGURE and left one WORD wrong. The morning after a pull, the chip
// still read **"Today"** over a near-zero spend figure that was two days old — and the misreading that
// produces is the most expensive one in the product: "my ads have stopped."
//
// The rule, in one line: **a preset whose name is a claim about WHICH DAY loses that name on the chip.**
//
//   · `today` / `yesterday` name a day, `this_month` / `last_month` name a month. Where the served range is
//     not the range the DEVICE's own clock would give that name, the chip reads the served day or month
//     instead — "Sep 30", "Sep 29", "September".
//   · `last_7d` / `last_30d` / `custom` name a LENGTH, not a day, so the name stays. A seven-day name over a
//     six-day answer is already answered by the dates beside it and their "Showing the dates this account
//     has numbers for. You asked for …" (D-610). No new words.
//   · **The menu row keeps the preset's name.** The name is what the owner is ASKING for; the chip is what
//     they are being shown. The URL, the saved preference and the preset's identity are untouched.
//
// It is not reachable on a demo account, where the served range always equals the ask (D-626) — see
// `PeriodProvider`, which arms it exactly where `useServerPeriod` is armed and for the same reason.

/** The four presets whose NAME is a claim about which day or month (REQ-021 AC6). */
export const DAY_CLAIMING_PRESETS: readonly PeriodPreset[] = [
  'today',
  'yesterday',
  'this_month',
  'last_month',
];

export function isDayClaimingPreset(preset: PeriodPreset): boolean {
  return DAY_CLAIMING_PRESETS.includes(preset);
}

// "September" is `@sl/core/format`'s `monthName` since T-339 (D-1229; R-3 of the slice-0.4 review): one helper, not a
// `date.month` row (D-166), so this app asks `Intl` for a month nowhere but `PeriodPopover`'s "September 2026".

export interface ChipNameInput {
  preset: PeriodPreset;
  /** The range on screen: the SERVED one where the server answered differently, else the resolved ask. */
  shown: Pick<ResolvedPeriod, 'since' | 'until'>;
  /**
   * The same preset resolved against the DEVICE's own day — what the name would mean to someone reading their
   * own calendar. `null` where the question does not apply (a demo account, D-626), which keeps the name.
   */
  device: Pick<ResolvedPeriod, 'since' | 'until'> | null;
  /** The preset's own name ("Today"), which is the answer in every case but the one this function is for. */
  presetLabel: string;
  /**
   * `date.short` through the account's own formatter — **"Sep 30", not `date.day`'s "Wed, Sep 30"**. The chip
   * is a control's label in a crowded top bar, AC6 writes it that way, and it is also the form the dates slot
   * beside it uses for a one-day range — which is what lets the control notice the repeat.
   */
  day: (date: IsoDate) => string;
  locale: string;
}

/**
 * What the CLOSED chip's name slot reads. The preset's own name, unless this is one of the four day-claiming
 * presets AND the served range is not the one the device's clock would give it — then the served day, or the
 * served month where the preset names a month.
 *
 * A month preset is named by its SINCE: `this_month` served Sep 1 – 30 on a device that is into October is
 * "September", and `last_month` served Aug 1 – 31 is "August".
 */
export function periodChipName(input: ChipNameInput): string {
  const { preset, shown, device, presetLabel } = input;
  if (device === null || !isDayClaimingPreset(preset)) return presetLabel;
  if (device.since === shown.since && device.until === shown.until) return presetLabel;
  if (preset === 'this_month' || preset === 'last_month')
    return monthName(shown.since, input.locale) || presetLabel;
  return input.day(shown.until) || presetLabel;
}
